import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import MultiFileUpload from "@/components/MultiFileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls, Field } from "./ui";
import { toast } from "sonner";

const blank = { title: "", description: "", cover_path: null, access: "free", price: 0, currency: "eur", status: "draft", modules: [], attachments: [] };
const blankLesson = { title: "", type: "text", text: "", media_kind: "none", media_url: "", media_path: null, downloads: [] };

export default function CourseManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/admin/courses").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const payload = { ...editing, price: parseFloat(editing.price) || 0 };
    try {
      if (editing.id) await api.put(`/admin/courses/${editing.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("Salvato"); setEditing(null); load();
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/admin/courses/${id}`); load(); };

  const upd = (fn) => setEditing((e) => { const c = structuredClone(e); fn(c); return c; });

  return (
    <div data-testid="cms-courses">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl">Corsi</h2>
        <button onClick={() => setEditing(structuredClone(blank))} data-testid="cms-new-course"
          className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-[#B4718C]">
          <Plus className="w-4 h-4" /> Nuovo
        </button>
      </div>

      {items.length === 0 ? <p className="text-[#9C8F94] py-10 text-center">Nessun corso.</p> : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-4 bg-[#FBF5F1] border border-[rgba(61,50,56,0.08)] rounded-xl p-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#F3E8E2] shrink-0">{c.cover_path ? <img src={mediaUrl(c.cover_path)} alt="" className="w-full h-full object-cover" /> : null}</div>
              <div className="flex-1 min-w-0"><div className="text-[#3D3238] truncate">{c.title}</div><div className="text-xs text-[#C6899E] mt-1">{c.access} · {(c.modules || []).length} moduli · {c.status === "published" ? "Pubblicato" : "Bozza"}</div></div>
              <button onClick={() => setEditing(structuredClone(c))} className="p-2 text-[#8C7E83] hover:text-[#C6899E]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(c.id)} className="p-2 text-[#8C7E83] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[rgba(198,137,158,0.2)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#3D3238]">{editing?.id ? "Modifica" : "Nuovo"} corso</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <Field label="Titolo"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="cms-course-title" /></Field>
              <Field label="Descrizione"><textarea rows={3} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
              <FileUpload label="Copertina" value={editing.cover_path} onChange={(p) => setEditing({ ...editing, cover_path: p })} testid="cms-course-cover" />

              <MultiFileUpload label="File del corso — caricali qui senza dover creare moduli (PDF, audio, video...)" value={editing.attachments} onChange={(v) => setEditing({ ...editing, attachments: v })} testid="cms-course-attachments" />
              <div className="grid grid-cols-3 gap-4">
                <Field label="Accesso">
                  <select className={inputCls} value={editing.access} onChange={(e) => setEditing({ ...editing, access: e.target.value })} data-testid="cms-course-access">
                    <option value="free">Gratis</option><option value="purchase">Acquisto</option><option value="premium">Premium</option>
                  </select>
                </Field>
                <Field label="Prezzo (€)"><input type="number" step="0.01" className={inputCls} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} disabled={editing.access !== "purchase"} /></Field>
                <Field label="Stato">
                  <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    <option value="draft">Bozza</option><option value="published">Pubblicato</option>
                  </select>
                </Field>
              </div>

              {/* Modules */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.12em] text-[#9C8F94]">Moduli</span>
                  <button onClick={() => upd((c) => c.modules.push({ title: "", lessons: [] }))} data-testid="cms-add-module"
                    className="text-xs text-[#C6899E] inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Modulo</button>
                </div>
                {editing.modules.map((m, mi) => (
                  <div key={mi} className="rounded-xl border border-[rgba(61,50,56,0.1)] p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <input className={inputCls} placeholder={`Modulo ${mi + 1}`} value={m.title} onChange={(e) => upd((c) => c.modules[mi].title = e.target.value)} data-testid={`cms-module-title-${mi}`} />
                      <button onClick={() => upd((c) => c.modules.splice(mi, 1))} className="p-2 text-[#9C8F94] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="pl-4 space-y-3 border-l border-[rgba(198,137,158,0.2)]">
                      {m.lessons.map((l, li) => (
                        <div key={li} className="rounded-lg bg-[#FBF5F1] p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <input className={inputCls} placeholder="Titolo lezione" value={l.title} onChange={(e) => upd((c) => c.modules[mi].lessons[li].title = e.target.value)} data-testid={`cms-lesson-title-${mi}-${li}`} />
                            <select className={inputCls + " w-32"} value={l.type} onChange={(e) => upd((c) => c.modules[mi].lessons[li].type = e.target.value)}>
                              <option value="text">Testo</option><option value="video">Video</option><option value="audio">Audio</option><option value="download">Download</option>
                            </select>
                            <button onClick={() => upd((c) => c.modules[mi].lessons.splice(li, 1))} className="p-2 text-[#9C8F94] hover:text-red-400"><X className="w-4 h-4" /></button>
                          </div>
                          {l.type === "text" && <textarea rows={3} className={inputCls} placeholder="Contenuto" value={l.text} onChange={(e) => upd((c) => c.modules[mi].lessons[li].text = e.target.value)} />}
                          {(l.type === "video" || l.type === "audio") && (
                            <div className="grid grid-cols-2 gap-2">
                              <select className={inputCls} value={l.media_kind} onChange={(e) => upd((c) => c.modules[mi].lessons[li].media_kind = e.target.value)}>
                                <option value="none">—</option><option value="upload">Upload</option><option value="youtube">YouTube</option><option value="vimeo">Vimeo</option><option value="spotify">Spotify</option><option value="soundcloud">SoundCloud</option>
                              </select>
                              {l.media_kind === "upload"
                                ? <FileUpload accept={l.type === "audio" ? "audio/*" : "video/*"} value={l.media_path} onChange={(p) => upd((c) => c.modules[mi].lessons[li].media_path = p)} />
                                : l.media_kind !== "none" ? <input className={inputCls} placeholder="URL" value={l.media_url} onChange={(e) => upd((c) => c.modules[mi].lessons[li].media_url = e.target.value)} /> : null}
                            </div>
                          )}
                          {l.type === "download" && (
                            <FileUpload accept="*" value={null} onChange={(p) => upd((c) => c.modules[mi].lessons[li].downloads.push(p))} />
                          )}
                        </div>
                      ))}
                      <button onClick={() => upd((c) => c.modules[mi].lessons.push(structuredClone(blankLesson)))} data-testid={`cms-add-lesson-${mi}`}
                        className="text-xs text-[#C6899E] inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Lezione</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[rgba(61,50,56,0.08)]">
                <button onClick={() => setEditing(null)} className="text-sm text-[#9C8F94] px-4 py-2">Annulla</button>
                <button onClick={save} disabled={saving || !editing.title} data-testid="cms-save-course"
                  className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-6 py-2.5 text-sm uppercase tracking-[0.12em] hover:bg-[#B4718C] disabled:opacity-40">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
